import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Loader2, Printer } from 'lucide-react'
import toast from 'react-hot-toast'

import { useAuthStore } from '@/store/auth.store'
import { invoicesService } from '@/services/invoicesService'
import { customersService } from '@/services/customersService'
import { branchService } from '@/services/branchService'
import { projectsService } from '@/services/projectsService'
import { BranchEntity, Customer, Invoice, InvoiceLine } from '@/api/types'
import { Button } from '@/components/ui/button'
import { getLogoPath } from '@/components/shared/Logo'

// NOTE: This component is an updated version that reproduces the PDF's printed layout
// (positions, borders, Arabic/English columns, logo placement, heavy ruled line, table layout,
// colors, and QR placement). It includes print-specific CSS inside a <style> tag so the
// rendered print output closely matches the provided Report.pdf.

const formatDate = (value?: string) => {
  if (!value) return '-'
  try {
    const date = new Date(value)
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch {
    return value
  }
}

const formatAmount = (value?: number) => {
  if (value === undefined || value === null || Number.isNaN(value)) return '0.00'
  return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const buildAddress = (entity?: Partial<BranchEntity> | Partial<Customer> | null) => {
  if (!entity) return ''
  const source = entity as Record<string, any>
  const parts = [
    source.address,
    source.addressEn,
    source.addressAr,
    source.street,
    source.citySubdivision,
    source.citySubdivisionAr,
    source.cityName,
    source.cityNameAr,
    source.city,
    source.countryName,
    source.countryNameAr,
    source.countryAr,
    source.zipCode,
    source.postalCode,
  ]
  return parts.filter((part) => typeof part === 'string' && part.trim().length > 0).join(', ')
}


// number-to-words helpers (kept minimal and robust)
const ONES = [
  'Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
  'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'
]
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety']
const SCALE = ['', 'Thousand', 'Million', 'Billion', 'Trillion']

const convertHundreds = (num: number): string => {
  let result: string[] = []
  if (num >= 100) {
    result.push(`${ONES[Math.floor(num / 100)]} Hundred`)
    num %= 100
  }
  if (num >= 20) {
    result.push(TENS[Math.floor(num / 10)])
    if (num % 10) result.push(ONES[num % 10])
  } else if (num > 0) {
    result.push(ONES[num])
  }
  return result.join(' ')
}

const convertIntegerToWords = (value: number) => {
  if (value === 0) return ONES[0]
  let num = value
  const chunks: string[] = []
  let scaleIndex = 0
  while (num > 0) {
    const chunk = num % 1000
    if (chunk) {
      const chunkWords = convertHundreds(chunk)
      const scaleWord = SCALE[scaleIndex]
      chunks.unshift(`${chunkWords}${scaleWord ? ` ${scaleWord}` : ''}`.trim())
    }
    num = Math.floor(num / 1000)
    scaleIndex++
  }
  return chunks.join(' ')
}

const amountToWords = (amount?: number) => {
  if (amount === undefined || amount === null || Number.isNaN(amount)) return ''
  const absolute = Math.abs(amount)
  const integerPart = Math.floor(absolute)
  const fractionPart = Math.round((absolute - integerPart) * 100)
  const integerWords = convertIntegerToWords(integerPart)
  let words = `${integerWords} Saudi Riyals`
  if (fractionPart > 0) words += ` and ${convertIntegerToWords(fractionPart)} Halalas`
  return `${words} Only`
}

const findQrImage = async (invoiceData: any): Promise<string | null> => {
  if (!invoiceData?.qrCode) return null
  try {
    const QRCode = await import('qrcode')
    const qrCodeData = String(invoiceData.qrCode)
    const dataUrl = await QRCode.toDataURL(qrCodeData, {
      errorCorrectionLevel: 'M',
      margin: 1,
      width: 280,
      color: { dark: '#000000', light: '#FFFFFF' },
    })
    return dataUrl
  } catch (error) {
    console.error('QR gen error', error)
    return null
  }
}

export default function InvoicePrintUpdated(): JSX.Element {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [invoice, setInvoice] = useState<Invoice | null>(null)
  const [lines, setLines] = useState<InvoiceLine[]>([])
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [branch, setBranch] = useState<BranchEntity | null>(null)
  const [qrImage, setQrImage] = useState<string | null>(null)
  const [branchCityName, setBranchCityName] = useState<string>('')
  const [customerCityName, setCustomerCityName] = useState<string>('')
  const [projectName, setProjectName] = useState<string>('')

  useEffect(() => {
    if (!branchId || !id) {
      setError('Missing branch or invoice identifier')
      setLoading(false)
      return
    }
    const invoiceId = Number(id)
    if (Number.isNaN(invoiceId)) {
      setError('Invalid invoice id')
      setLoading(false)
      return
    }

    const loadInvoice = async () => {
      try {
        setLoading(true)
        setError(null)
        const invoiceResponse = await invoicesService.getInvoiceById(branchId, invoiceId)
        const linesData = await invoicesService.getInvoiceLinesById(branchId, invoiceId).catch(() => [])
        setInvoice(invoiceResponse)
        setLines(Array.isArray(linesData) ? linesData : [])
        const [customerData, branchData, qrData] = await Promise.all([
          invoiceResponse.customerId ? customersService.getCustomerById(branchId, invoiceResponse.customerId).catch(() => null) : null,
          branchService.getBranchById(branchId).catch(() => null),
          findQrImage(invoiceResponse),
        ])
        setCustomer(customerData)
        setBranch(branchData)
        setQrImage(qrData)

        // Load project name if projectId exists and is valid
        const projectId = invoiceResponse.projectId
        console.log('Invoice projectId from response:', projectId, 'Type:', typeof projectId)
        if (projectId && Number(projectId) > 0) {
          try {
            const projectIdNum = Number(projectId)
            console.log('Loading project for ID:', projectIdNum, 'branchId:', branchId)
            const projectData = await projectsService.getProjectById(projectIdNum, branchId)
            console.log('Project data loaded:', projectData)
            const name = projectData.name || projectData.projectName || projectData.projectNameEn || projectData.projectNameAr || projectData.code || ''
            console.log('Project name extracted:', name)
            setProjectName(name || '-')
          } catch (err: any) {
            console.error('Failed to load project:', err)
            console.error('Error details:', err?.response?.data || err?.message)
            setProjectName('-')
          }
        } else {
          console.log('No valid projectId found. projectId value:', projectId)
          setProjectName('-')
        }

        if (branchData?.country && branchData?.city) {
          try {
            const branchCities = await branchService.getCityLookups(branchData.country)
            const branchCity = branchCities.find((c: any) => c.id === branchData.city)
            if (branchCity) setBranchCityName(branchCity.lookupNameEn || '')
          } catch (err) { console.error('branch city', err) }
        }

        if (customerData?.country && customerData?.city) {
          try {
            const customerCities = await customersService.getCities(customerData.country)
            const customerCity = customerCities.find((c: any) => c.id === customerData.city)
            if (customerCity) setCustomerCityName(customerCity.lookupNameEn || '')
          } catch (err) { console.error('customer city', err) }
        }
      } catch (err: any) {
        console.error('Failed to load invoice for print', err)
        const message = err?.response?.data?.message || err?.message || 'Unable to load invoice details'
        setError(message)
        toast.error(message)
      } finally {
        setLoading(false)
      }
    }

    loadInvoice()
  }, [branchId, id])

  const totals = useMemo(() => {
    const validLines = lines || []
    const subTotal = validLines.reduce((sum, line) => sum + (line.itemPrice ?? ((line.qty || 0) * (line.unitPrice || 0))), 0)
    const totalDiscount = validLines.reduce((sum, line) => sum + (line.discountAmount ?? 0), 0)
    const taxableAmount = validLines.reduce((sum, line) => sum + (line.taxableAmount ?? 0), 0)
    const vatAmount = validLines.reduce((sum, line) => sum + (line.taxAmount ?? 0), 0)
    const grandTotal = invoice?.total ?? validLines.reduce((sum, line) => sum + (line.lineAmount ?? line.taxableAmount ?? 0), 0)
    return { subTotal, totalDiscount, taxableAmount, vatAmount, total: grandTotal }
  }, [lines, invoice])

  const amountWords = useMemo(() => amountToWords(totals.total), [totals.total])

  if (loading) return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Preparing invoice for print...</p>
      </div>
    </div>
  )

  if (error || !invoice) return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 p-6 text-center">
      <p className="mb-4 text-lg font-semibold text-destructive">{error || 'Invoice not found'}</p>
      <Button onClick={() => navigate('/invoices')}>Back to Invoices</Button>
    </div>
  )

  return (
    <div className="invoice-print-root p-6 bg-gray-50 print:bg-white print:p-0 print:m-0">
      {/* Print styles embedded so the PDF-like design is enforced on print */}
      <style>{`
        @media print {
          @page { 
            size: A4; 
            margin: 8mm;
          }
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          html, body {
            height: auto;
            overflow: hidden;
            margin: 0;
            padding: 0;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .no-print { display: none !important }
          .invoice-print-root {
            margin: 0;
            padding: 0;
            height: 100vh;
            border: 1px solid #ddd;
            position: relative;
          }
          .invoice-wrapper {
            border: 1px solid #ddd;
            padding: 10px;
            min-height: calc(100vh - 16mm);
            display: flex;
            flex-direction: column;
            position: relative;
            padding-bottom: 30px;
            margin: 0;
          }
          .invoice-content {
            flex: 1;
          }
          table { 
            page-break-inside: auto;
          }
          tr { 
            page-break-inside: avoid;
            page-break-after: auto;
          }
          thead { display: table-header-group; }
          tfoot { display: table-footer-group; }
          .invoice-footer {
            position: fixed;
            bottom: 8mm;
            left: 8mm;
            right: 8mm;
            border-top: 1px dashed #999;
            padding-top: 4px;
            padding-bottom: 4px;
            text-align: center;
            font-weight: 600;
            font-size: 9px;
            background: white;
            z-index: 1000;
          }
        }

        .invoice-print-root { font-family: 'Arial', 'Noto Naskh Arabic', sans-serif; color: #1f2937; border: 1px solid #ddd; }
        .invoice-wrapper { max-width: 100%; margin: 0 auto; background: #fff; padding: 10px; border: none; min-height: calc(100vh - 40px); display: flex; flex-direction: column; }
        .invoice-content { flex: 1; }
        .top-brand { display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom: 4px }
        .company-title { font-weight:700; font-size:14px; letter-spacing:0.2px; text-align:center }
        .invoice-main-title { text-align:center; font-size:16px; font-weight:800; margin-top:4px }
        .divider-strong { height:3px; background:#0A1E60; margin:6px 0; }

        /* Header info table */
        .info-table { width:100%; border-collapse:collapse; font-size:10px; margin-bottom: 4px }
        .info-table td { vertical-align:top; padding:3px; border:1px solid #F5F5F5 }
        .info-label { background:#F5F5F5; font-weight:700 }

        /* Seller/Buyer boxes */
        .party-box { border:1px solid #F5F5F5; border-radius:4px; padding:6px }
        .party-box.gray { background:#f6f6f6 }
        .party-title { font-weight:700; margin-bottom:4px; font-size:10px }
        .party-row { display:flex; gap:4px; margin-bottom:3px }

        /* Items table */
        .items-table { width:100%; border-collapse:collapse; font-size:9px; margin-top:4px }
        .items-table th, .items-table td { border:1px solid #F5F5F5; padding:3px; }
        .items-table thead th { background:#F5F5F5; font-weight:700; font-size:9px }
        .items-table tbody tr td { vertical-align:middle; font-size:9px }
        .items-total-row { background:#F5F5F5; font-weight:800 }

        /* Summary box */
        .summary-box { border:1px solid #F5F5F5; padding:6px; border-radius:4px; font-size:9px }

        /* Footer */
        .invoice-footer { border-top:1px dashed #999; padding-top:4px; margin-top:4px; text-align:center; font-weight:600; font-size:9px }
        @media print {
          .invoice-footer {
            position: fixed;
            bottom: 8mm;
            left: 8mm;
            right: 8mm;
            border-top: 1px dashed #999;
            padding-top: 4px;
            text-align: center;
            font-weight: 600;
            font-size: 9px;
            background: white;
            z-index: 1000;
          }
        }

        /* View mode - make it compact like print */
        @media screen {
          .invoice-wrapper { max-width: 820px; padding: 12px }
          .top-brand { margin-bottom: 6px }
          .company-title { font-size: 16px }
          .invoice-main-title { font-size: 18px; margin-top: 6px }
          .divider-strong { margin: 8px 0 }
          .info-table { font-size: 11px; margin-bottom: 6px }
          .info-table td { padding: 4px }
          .items-table { font-size: 10px; margin-top: 6px }
          .items-table th, .items-table td { padding: 4px }
          .items-table thead th { font-size: 10px }
          .items-table tbody tr td { font-size: 10px }
          .summary-box { padding: 8px; font-size: 10px }
          .invoice-footer { padding-top: 6px; margin-top: 6px; font-size: 10px }
        }

        /* Arabic Right-to-left fixes */
        .rtl { direction: rtl; unicode-bidi: embed }

        /* Ensure QR fixed size */
        .qr-image { width:110px; height:110px; object-fit:contain; border:1px solid #F5F5F5 }
        @media print {
          .qr-image { width:80px; height:80px }
        }

      `}</style>

      <div className="no-print mb-4 flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <Button onClick={() => window.print()}>
          <Printer className="h-4 w-4" /> Print Invoice
        </Button>
      </div>

      <div className="invoice-wrapper">
        <div className="invoice-content">
        {/* header */}
        <div className="top-brand">
          <div style={{ width: '30%', fontSize: 11 }} className="print:text-[10px]">
            <div><strong>CR No:</strong> {branch?.crNo || '-'}</div>
            <div><strong>VAT No:</strong> {branch?.vatNo || '-'}</div>
          </div>

          <div style={{ width: '40%', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <img src={getLogoPath()} alt="Default Logo" style={{ height: 55, objectFit: 'contain', margin: '0 auto' }} className="print:h-10" />
          </div>

          <div style={{ width: '30%', textAlign: 'right', fontSize: 11 }} className="print:text-[10px]">
            <div><strong>رقم تسجيل الشركة:</strong> {branch?.crNo || '-'}</div>
            <div><strong>رقم ضريبة القيمة المضافة:</strong> {branch?.vatNo || '-'}</div>
          </div>
        </div>

        <div className="divider-strong print:my-1" style={{ backgroundColor: '#0A1E60', height: '3px', margin: '8px 0' }} />

        {/* Tax Invoice Heading */}
        <div className="print:mb-2" style={{ textAlign: 'center', marginBottom: 6 }}>
          <div className="company-title print:text-base" style={{ fontSize: 18, fontWeight: 700, marginBottom: 3 }}>فاتورة ضريبة</div>
          <div className="company-title print:text-sm" style={{ fontSize: 16, fontWeight: 700 }}>Tax Invoice</div>
        </div>

        {/* Invoice info table with QR on the right */}
        <table className="info-table print:mb-2" style={{ marginBottom: 6 }}>
          <tbody>
            <tr>
              <td className="info-label" style={{ width: '20%' }}>Invoice #:</td>
              <td style={{ width: '30%' }}>{invoice.invoiceNo || invoice.invoiceNumber || invoice.invoiceId || `INV-${invoice.invoiceId}`}</td>
              <td rowSpan={5} style={{ width: '30%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ textAlign: 'right', flex: 1, borderRight: '1px solid #F5F5F5', paddingRight: 12 }} className="rtl">
                    <div className="print:text-xs"><strong>:رقم الفاتورة</strong></div>
                    <div style={{ marginTop: 6 }} className="print:mt-1 print:text-xs"><strong>:تاريخ إصدار الفاتورة</strong></div>
                    <div style={{ marginTop: 6 }} className="print:mt-1 print:text-xs"><strong>:تاريخ التوريد</strong></div>
                    <div style={{ marginTop: 6 }} className="print:mt-1 print:text-xs"><strong>:الشروط الدفع</strong></div>
                    <div style={{ marginTop: 6 }} className="print:mt-1 print:text-xs"><strong>:اسم المشروع</strong></div>
                  </div>
                  <div style={{ marginLeft: 12 }}>
                    {qrImage && <img src={qrImage} alt="qr" className="qr-image" />}
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td className="info-label">Invoice Issue Date:</td>
              <td>{formatDate(invoice.invoiceDate)}</td>
            </tr>
            <tr>
              <td className="info-label">Supply Date:</td>
              <td>{formatDate(invoice.supplyDate) || '-'}</td>
            </tr>
            <tr>
              <td className="info-label">Payment Term:</td>
              <td>{invoice.dueDate ? formatDate(invoice.dueDate) : (invoice.paymentTermId ? `Term #${invoice.paymentTermId}` : 'Due on Receipt')}</td>
            </tr>
            <tr>
              <td className="info-label">Project Name:</td>
              <td>{projectName || '-'}</td>
            </tr>
          </tbody>
        </table>

        {/* Seller / Buyer Table */}
        <table className="seller-buyer-table print:mb-2" style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #F5F5F5', marginBottom: 4 }}>
          <thead>
            <tr>
              <th className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', textAlign: 'left', fontWeight: 700, fontSize: 10, backgroundColor: '#F5F5F5' }} colSpan={2}>SELLER</th>
              <th className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', textAlign: 'right', fontWeight: 700, fontSize: 10, backgroundColor: '#F5F5F5' }}></th>
              <th className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', textAlign: 'right', fontWeight: 700, fontSize: 10, backgroundColor: '#F5F5F5' }} colSpan={3}>BUYER</th>
            </tr>
          </thead>
          <tbody>
            {/* Name Row */}
            <tr>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, width: '12%' }}><strong>Name:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, width: '20%', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                {branch?.nameEn || '-'}
                {branch?.nameAr && <div className="rtl print:text-[10px]" style={{ fontSize: 10, marginTop: 2, textAlign: 'right' }}>{branch.nameAr}</div>}
              </td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right', width: '8%' }}><strong>الاسم:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, width: '12%' }}><strong>Name:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, width: '20%', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                {customer?.customerNameEn || customer?.customerNameAr || 'Customer'}
                {customer?.customerNameAr && <div className="rtl print:text-[10px]" style={{ fontSize: 10, marginTop: 2, textAlign: 'right' }}>{customer.customerNameAr}</div>}
              </td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right', width: '8%' }}><strong>الاسم:</strong></td>
            </tr>
            {/* Address Row */}
            <tr>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11 }}><strong>Address:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' }}>
                {buildAddress(branch) || '-'}
                {branch?.addressAr && <div className="rtl print:text-[10px]" style={{ fontSize: 9, marginTop: 1, textAlign: 'right' }}>{branch.addressAr}</div>}
              </td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right' }}><strong>عنوان:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11 }}><strong>Address:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' }}>
                {customer?.address || customer?.addressEn || customer?.addressAr || '-'}
                {customer?.addressAr && <div className="rtl print:text-[10px]" style={{ fontSize: 9, marginTop: 1, textAlign: 'right' }}>{customer.addressAr}</div>}
              </td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right' }}><strong>عنوان:</strong></td>
            </tr>
            {/* Country Row */}
            <tr>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11 }}><strong>Country:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, wordWrap: 'break-word', overflowWrap: 'break-word' }}>{branch?.countryName || 'Saudi Arabia (SA)'}</td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right' }}><strong>دولة:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11 }}><strong>Country:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, wordWrap: 'break-word', overflowWrap: 'break-word' }}>{customer?.countryAr || 'Saudi Arabia (SA)'}</td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right' }}><strong>دولة:</strong></td>
            </tr>
            {/* City Row */}
            <tr>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11 }}><strong>City:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, wordWrap: 'break-word', overflowWrap: 'break-word' }}>{branchCityName || branch?.cityName || '-'}</td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right' }}><strong>مدينة:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11 }}><strong>City:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, wordWrap: 'break-word', overflowWrap: 'break-word' }}>{customerCityName || customer?.cityName || customer?.cityNameAr || customer?.cityAr || '-'}</td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right' }}><strong>مدينة:</strong></td>
            </tr>
            {/* Zip/Postal Code Row */}
            <tr>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11 }}><strong>Zip/Postal Code:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, wordWrap: 'break-word', overflowWrap: 'break-word' }}>{branch?.zipCode || '-'}</td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right' }}><strong>الرمز / الكود البريدي:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11 }}><strong>Zip/Postal Code:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, wordWrap: 'break-word', overflowWrap: 'break-word' }}>{customer?.zipCode || customer?.postalCode || '-'}</td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right' }}><strong>الرمز / الكود البريدي:</strong></td>
            </tr>
            {/* Phone Row */}
            <tr>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11 }}><strong>Phone:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, wordWrap: 'break-word', overflowWrap: 'break-word' }}>{branch?.phone || '-'}</td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right' }}><strong>هاتف:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11 }}><strong>Phone:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, wordWrap: 'break-word', overflowWrap: 'break-word' }}>{customer?.phone || customer?.mobileNo || '-'}</td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right' }}><strong>هاتف:</strong></td>
            </tr>
            {/* VAT No Row */}
            <tr>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11 }}><strong>VAT Number:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, wordWrap: 'break-word', overflowWrap: 'break-word' }}>{branch?.vatNo || '-'}</td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right' }}><strong>رقم ضريبة القيمة المضافة:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11 }}><strong>VAT Number:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, wordWrap: 'break-word', overflowWrap: 'break-word' }}>{customer?.vatNumber || customer?.vatNo || '-'}</td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right' }}><strong>رقم ضريبة القيمة المضافة:</strong></td>
            </tr>
            {/* Other ID Row */}
            <tr>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11 }}><strong>Other Seller Id:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, wordWrap: 'break-word', overflowWrap: 'break-word' }}>CR# {branch?.crNo || '-'}</td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right' }}><strong>معرف الآخر:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11 }}><strong>Other Buyer Id:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, wordWrap: 'break-word', overflowWrap: 'break-word' }}>CR# {customer?.companyRegistrationNumber || customer?.companyRegistrationNo || '-'}</td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right' }}><strong>معرف الآخر:</strong></td>
            </tr>
          </tbody>
        </table>

        {/* Items table */}
        <table className="items-table">
          <thead>
            <tr>
              <th style={{ width: '4%' }}>SR#</th>
              <th style={{ width: '36%' }}>ITEM & DESCRIPTION</th>
              <th style={{ width: '6%', textAlign: 'right' }}>QTY</th>
              <th style={{ width: '8%', textAlign: 'right' }}>RATE</th>
              <th style={{ width: '10%', textAlign: 'right' }}>BILL AMT.</th>
              <th style={{ width: '6%', textAlign: 'right' }}>DISC (%)</th>
              <th style={{ width: '8%', textAlign: 'right' }}>DISC AMT.</th>
              <th style={{ width: '10%', textAlign: 'right' }}>TAXABLE AMOUNT.</th>
              <th style={{ width: '6%', textAlign: 'right' }}>VAT (%)</th>
              <th style={{ width: '6%', textAlign: 'right' }}>VAT AMT.</th>
              <th style={{ width: '10%', textAlign: 'right' }}>TOTAL SAR</th>
            </tr>
            <tr>
              <th style={{ width: '4%' }}>رقم</th>
              <th style={{ width: '36%' }}>البند و الوصف</th>
              <th style={{ width: '6%', textAlign: 'right' }}>الكمية</th>
              <th style={{ width: '8%', textAlign: 'right' }}>السعر</th>
              <th style={{ width: '10%', textAlign: 'right' }}>مبلغ الفاتورة</th>
              <th style={{ width: '6%', textAlign: 'right' }}>الخصم (%)</th>
              <th style={{ width: '8%', textAlign: 'right' }}>الخصم</th>
              <th style={{ width: '10%', textAlign: 'right' }}>المبلغ الخاضع للضريبة</th>
              <th style={{ width: '6%', textAlign: 'right' }}>ضريبة</th>
              <th style={{ width: '6%', textAlign: 'right' }}>قيمة الضريبة</th>
              <th style={{ width: '10%', textAlign: 'right' }}>إجمالي</th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr><td colSpan={11} style={{ textAlign: 'center', padding: 16 }}>No line items available</td></tr>
            ) : (
              lines.map((line, idx) => (
                <tr key={line.lineId || idx}>
                  <td style={{ textAlign: 'center', fontWeight:700 }}>{idx+1}</td>
                  <td>{line.description || 'Item'}</td>
                  <td style={{ textAlign: 'right' }}>{formatAmount(line.qty ?? 0)}</td>
                  <td style={{ textAlign: 'right' }}>{formatAmount(line.unitPrice)}</td>
                  <td style={{ textAlign: 'right' }}>{formatAmount(line.itemPrice)}</td>
                  <td style={{ textAlign: 'right' }}>{formatAmount(line.discountPercent)}</td>
                  <td style={{ textAlign: 'right' }}>{formatAmount(line.discountAmount)}</td>
                  <td style={{ textAlign: 'right' }}>{formatAmount(line.taxableAmount)}</td>
                  <td style={{ textAlign: 'right' }}>{formatAmount(line.taxRate ?? line.taxPercent ?? 0)}</td>
                  <td style={{ textAlign: 'right' }}>{formatAmount(line.taxAmount)}</td>
                  <td style={{ textAlign: 'right', fontWeight:700 }}>{formatAmount(line.lineAmount)}</td>
                </tr>
              ))
            )}

            {/* totals row */}
            <tr className="items-total-row">
              <td colSpan={4} style={{ textAlign: 'right' }}>TOTAL</td>
              <td style={{ textAlign: 'right' }}>{formatAmount(totals.subTotal)}</td>
              <td colSpan={2}></td>
              <td style={{ textAlign: 'right' }}>{formatAmount(totals.taxableAmount)}</td>
              <td></td>
              <td style={{ textAlign: 'right' }}>{formatAmount(totals.vatAmount)}</td>
              <td style={{ textAlign: 'right' }}>{formatAmount(totals.total)}</td>
            </tr>
          </tbody>
        </table>

        {/* Notes / Bank details / Summary */}
        <div style={{ display:'grid', gridTemplateColumns: '1fr 340px', gap:6, marginTop:0 }} className="print:gap-2 print:mt-0">
          <div>
            <div style={{ fontWeight:700, marginBottom:3, fontSize:9 }} className="print:text-xs print:mb-1"><strong>ملاحظات - Notes</strong></div>
            <div style={{ fontSize:9 }} className="print:text-xs">{invoice.note || invoice.refNo || invoice.refNumber || invoice.poNumber || '—'}</div>

            <div style={{ marginTop:4 }} className="print:mt-2">
              <div style={{ fontWeight:700, fontSize:9 }} className="print:text-xs"><strong>Amount In Words:</strong></div>
              <div style={{ fontWeight:700, fontSize:9 }} className="print:text-xs">{amountWords || 'Zero Saudi Riyals Only'}</div>
              <div style={{ fontSize:8, color:'#555', marginTop:2 }} className="print:text-[10px] print:mt-1">(All Amount in SAR)</div>
            </div>

            {branch?.bankDetailEn && (
              <div style={{ marginTop:4 }} className="print:mt-2">
                <div style={{ fontWeight:700, fontSize:9 }} className="print:text-xs"><strong>Bank Detail</strong></div>
                <div style={{ whiteSpace:'pre-line', fontSize:9 }} className="print:text-xs">{branch.bankDetailEn}</div>
              </div>
            )}
          </div>

          <div className="summary-box print:p-2" style={{ border:'1px solid #F5F5F5', padding:6 }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3, fontSize:9 }} className="print:text-xs print:mb-1">
              <div style={{ fontWeight:700 }}>المجموع الفرعي - Sub Total</div>
              <div style={{ fontWeight:700 }}>{formatAmount(totals.subTotal)}</div>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3, fontSize:9 }} className="print:text-xs print:mb-1">
              <div style={{ fontWeight:700 }}>Discount - خصم</div>
              <div style={{ fontWeight:700 }}>{formatAmount(totals.totalDiscount)}</div>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3, fontSize:9 }} className="print:text-xs print:mb-1">
              <div style={{ fontWeight:700 }}>المبلغ الخاضع للضريبة - Taxable Amount</div>
              <div style={{ fontWeight:700 }}>{formatAmount(totals.taxableAmount)}</div>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3, fontSize:9 }} className="print:text-xs print:mb-1">
              <div style={{ fontWeight:700 }}>ضريبة القيمة المضافة - VAT Amount</div>
              <div style={{ fontWeight:700 }}>{formatAmount(totals.vatAmount)}</div>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3, fontSize:9 }} className="print:text-xs print:mb-1">
              <div style={{ fontWeight:700 }}>ضمان حسن التنفيذ - Retention</div>
              <div style={{ fontWeight:700 }}>0%</div>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', borderTop:'2px solid #F5F5F5', paddingTop:4, marginTop:4, fontSize:10, fontWeight:800 }} className="print:text-xs print:pt-2 print:mt-2">
              <div>إجمالي المبلغ المستحق - Total Amount Due</div>
              <div>{formatAmount(totals.total)}</div>
            </div>
          </div>
        </div>
        </div>

        <div className="invoice-footer">This invoice was generated electronically and does not require any signature. | تم إنشاء هذه الفاتورة إلكترونيًا ولا تحتاج إلى توقيع.</div>

      </div>
    </div>
  )
}
